import { ConcreteEntityRights } from "@/types/domain";
import { ConcreteEntityRightType } from "@/types/rights";
import { domainTools } from "@lib/domain";
import { localizeFrom } from "@lib/i18n/utils";
import { ContributorBriefOutputView } from "@features/contributors/types/contributor";
import { ProposalCommentEntityRightsTypes } from "@features/events-program/types/rights";
import {
  ProposalCommentBaseOutputView,
  ProposalCommentPreviewOutputView,
  ProposalCommentReplyEntity,
  ProposalCommentRootEntity,
  ProposalCommentThreadEntity,
} from "@features/events-program/types/proposal-comment";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { TeamMemberAggregateEntity } from "@features/teams/types/team";

export type ProposalCommentDisplayItem<T extends ProposalCommentBaseOutputView> = T & {
  authorDisplayName: string;
  authorInitials: string;
  authorPhotoUrl?: string;
  rights?: ConcreteEntityRights<ProposalCommentEntityRightsTypes>;
};

export type ProposalCommentDisplayThread = {
  root: ProposalCommentDisplayItem<ProposalCommentRootEntity>;
  replies: ProposalCommentDisplayItem<ProposalCommentReplyEntity>[];
  resolved: boolean;
};

export type ProposalCommentAuthorDisplayOptions = {
  unknownAuthorLabel?: string;
  locale?: string;
};

export type ProposalCommentAuthorLookup = Map<number, ContributorBriefOutputView>;
export type ProposalCommentTeamMemberAuthorLookup = Map<number, TeamMemberAggregateEntity>;
export type ProposalCommentAuthorReference = Pick<
  ProposalCommentBaseOutputView,
  "authorContributorId" | "authorMemberId"
>;

export type ProposalCommentThreadsOptions = ProposalCommentAuthorDisplayOptions & {
  authors?: ContributorBriefOutputView[];
  authorTeamMemberAggregates?: TeamMemberAggregateEntity[];
  includeResolved?: boolean;
};

export type ProposalCommentTimeLabelOptions = {
  locale: string;
  now: Date;
};

type ProposalCommentEditCandidate = {
  authorContributorId?: number;
  createdAt?: Date | string;
  rights?: { canPatch?: ConcreteEntityRightType };
};

export const PROPOSAL_COMMENT_EDIT_WINDOW_MS = 5 * 60 * 1000;

export const getProposalCommentEditDeadline = (createdAt?: Date | string) => {
  const createdTime = toDate(createdAt)?.getTime();
  return createdTime === undefined ? undefined : createdTime + PROPOSAL_COMMENT_EDIT_WINDOW_MS;
};

export const canEditProposalComment = (
  comment: ProposalCommentEditCandidate,
  currentContributorId: number | undefined,
  threadResolved: boolean,
  now: Date,
) => {
  if (
    currentContributorId === undefined ||
    comment.authorContributorId !== currentContributorId ||
    comment.rights?.canPatch !== true ||
    threadResolved
  ) {
    return false;
  }

  const deadline = getProposalCommentEditDeadline(comment.createdAt);
  return deadline !== undefined && Number.isFinite(now.getTime()) && now.getTime() < deadline;
};

export type ProposalLastCommentPreviewDisplay = ProposalCommentAuthorDisplay & {
  id: number;
  text: string;
  timeLabel: string;
  searchText: string;
  dateTime?: string;
};

export type ProposalLastCommentPreviewDisplayOptions = ProposalCommentAuthorDisplayOptions &
  ProposalCommentTimeLabelOptions & {
    authors?: ContributorBriefOutputView[];
    authorTeamMemberAggregates?: TeamMemberAggregateEntity[];
    emptyTextLabel: string;
  };

type ProposalCommentAuthorDisplay = {
  authorDisplayName: string;
  authorInitials: string;
  authorPhotoUrl?: string;
};

const DEFAULT_UNKNOWN_AUTHOR_LABEL = "Unknown author";
const COMMENT_AUTHOR_BIOGRAPHY_PHOTO_DISPLAY_SIZE_PX = 62;
const COMMENT_AUTHOR_AVATAR_IMAGE_TRANSFORM = {
  width: COMMENT_AUTHOR_BIOGRAPHY_PHOTO_DISPLAY_SIZE_PX * 2,
  height: COMMENT_AUTHOR_BIOGRAPHY_PHOTO_DISPLAY_SIZE_PX * 2,
  mode: "CropUpsize",
};

const normalizeText = (value?: string | null) => value?.trim() ?? "";

const firstNonEmptyText = (...values: Array<string | undefined | null>) =>
  values.map(normalizeText).find(Boolean) ?? "";

const getFallbackInitials = (authorId?: number) => authorId?.toString().slice(-2) || "?";

const getInitials = (displayName: string, fallback?: string) => {
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || fallback || "?";
};

const getLocalizedTextWithFallback = (
  value: Parameters<typeof localizeFrom>[0],
  locale: string | undefined,
  fallback?: string,
) => normalizeText(localizeFrom(value, locale ?? "ru", fallback)) || normalizeText(fallback);

const getTeamMemberPhotoUrl = (aggregate?: TeamMemberAggregateEntity) => {
  const biographyPhoto = proposalsTools.activityMemberTools.extractBiographyImages(
    aggregate?.eventProfile?.biography?.content,
    COMMENT_AUTHOR_BIOGRAPHY_PHOTO_DISPLAY_SIZE_PX,
  )?.[0];
  const aggregatePhoto = aggregate?.member?.contributor?.profile?.photo;

  return firstNonEmptyText(biographyPhoto, aggregatePhoto?.thumbnailUrl, aggregatePhoto?.photoUrl);
};

const toDate = (value?: Date | string) => {
  if (!value) return undefined;

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
};

const toTime = (value?: Date | string) => toDate(value)?.getTime();

const compareByCreatedAtDescIdDesc = (
  left: ProposalCommentDisplayThread,
  right: ProposalCommentDisplayThread,
) => {
  const leftTime = toTime(left.root.createdAt);
  const rightTime = toTime(right.root.createdAt);

  if (leftTime !== undefined && rightTime !== undefined && leftTime !== rightTime) {
    return rightTime - leftTime;
  }

  if (leftTime !== undefined && rightTime === undefined) return -1;
  if (leftTime === undefined && rightTime !== undefined) return 1;

  return right.root.id - left.root.id;
};

const compareByCreatedAtAscIdAsc = (
  left: ProposalCommentDisplayItem<ProposalCommentReplyEntity>,
  right: ProposalCommentDisplayItem<ProposalCommentReplyEntity>,
) => {
  const leftTime = toTime(left.createdAt);
  const rightTime = toTime(right.createdAt);

  if (leftTime !== undefined && rightTime !== undefined && leftTime !== rightTime) {
    return leftTime - rightTime;
  }

  if (leftTime !== undefined && rightTime === undefined) return -1;
  if (leftTime === undefined && rightTime !== undefined) return 1;

  return left.id - right.id;
};

export const getProposalCommentAuthorDisplayName = (
  comment: Pick<ProposalCommentBaseOutputView, "authorContributorId">,
  authors: ContributorBriefOutputView[] | ProposalCommentAuthorLookup = [],
  { unknownAuthorLabel = DEFAULT_UNKNOWN_AUTHOR_LABEL }: ProposalCommentAuthorDisplayOptions = {},
) => {
  const author =
    authors instanceof Map
      ? authors.get(comment.authorContributorId ?? -1)
      : authors.find(({ id }) => id === comment.authorContributorId);

  return author?.displayName || unknownAuthorLabel;
};

export const createProposalCommentAuthorLookup = (authors: ContributorBriefOutputView[] = []) =>
  new Map(authors.map((author) => [author.id, author]));

export const createProposalCommentTeamMemberAuthorLookup = (
  aggregates: TeamMemberAggregateEntity[] = [],
) =>
  new Map(
    aggregates
      .map((aggregate) => [aggregate.member?.id, aggregate] as const)
      .filter(
        (entry): entry is readonly [number, TeamMemberAggregateEntity] =>
          typeof entry[0] === "number",
      ),
  );

export const getMissingProposalCommentAuthorContributorIds = (
  comments: readonly ProposalCommentAuthorReference[] = [],
  teamMemberAggregates: readonly TeamMemberAggregateEntity[] = [],
) => {
  const teamMemberIds = new Set(
    teamMemberAggregates
      .map((aggregate) => aggregate.member?.id)
      .filter((memberId): memberId is number => typeof memberId === "number"),
  );
  const contributorIds = new Set<number>();

  comments.forEach((comment) => {
    if (typeof comment.authorContributorId !== "number") {
      return;
    }

    if (typeof comment.authorMemberId === "number" && teamMemberIds.has(comment.authorMemberId)) {
      return;
    }

    contributorIds.add(comment.authorContributorId);
  });

  return Array.from(contributorIds);
};

const getTeamMemberAuthorName = (aggregate: TeamMemberAggregateEntity, locale?: string) => {
  const biography = aggregate.eventProfile?.biography?.content?.data;
  const contributorProfile = aggregate.member?.contributor?.profile;
  const firstName = getLocalizedTextWithFallback(
    biography?.firstName,
    locale,
    contributorProfile?.name?.firstName,
  );
  const lastName = getLocalizedTextWithFallback(
    biography?.lastName,
    locale,
    contributorProfile?.name?.lastName,
  );

  return [firstName, lastName].filter(Boolean).join(" ");
};

const getProposalCommentAuthorDisplay = (
  comment: Pick<ProposalCommentBaseOutputView, "authorContributorId" | "authorMemberId">,
  authorsLookup: ProposalCommentAuthorLookup,
  teamMemberAuthorsLookup: ProposalCommentTeamMemberAuthorLookup,
  options: ProposalCommentAuthorDisplayOptions,
): ProposalCommentAuthorDisplay => {
  const contributor =
    typeof comment.authorContributorId === "number"
      ? authorsLookup.get(comment.authorContributorId)
      : undefined;
  const teamMemberAggregate =
    typeof comment.authorMemberId === "number"
      ? teamMemberAuthorsLookup.get(comment.authorMemberId)
      : undefined;
  const teamMemberDisplayName = teamMemberAggregate
    ? getTeamMemberAuthorName(teamMemberAggregate, options.locale)
    : undefined;
  const displayName = firstNonEmptyText(
    teamMemberDisplayName,
    contributor?.displayName,
    options.unknownAuthorLabel ?? DEFAULT_UNKNOWN_AUTHOR_LABEL,
  );
  const photoUrl = domainTools.imageActions.getOptimizedBackendImageUrl(
    firstNonEmptyText(getTeamMemberPhotoUrl(teamMemberAggregate), contributor?.photoUrl),
    COMMENT_AUTHOR_AVATAR_IMAGE_TRANSFORM,
  );
  const fallbackInitials = getFallbackInitials(
    comment.authorMemberId ?? comment.authorContributorId,
  );

  return {
    authorDisplayName: displayName,
    authorInitials: getInitials(displayName, fallbackInitials),
    ...(photoUrl ? { authorPhotoUrl: photoUrl } : {}),
  };
};

const withAuthorDisplayName = <T extends ProposalCommentBaseOutputView>(
  comment: T,
  authorsLookup: ProposalCommentAuthorLookup,
  teamMemberAuthorsLookup: ProposalCommentTeamMemberAuthorLookup,
  options: ProposalCommentAuthorDisplayOptions,
): ProposalCommentDisplayItem<T> => ({
  ...comment,
  ...getProposalCommentAuthorDisplay(comment, authorsLookup, teamMemberAuthorsLookup, options),
});

export const buildProposalCommentThreads = (
  threads: ProposalCommentThreadEntity[] = [],
  options: ProposalCommentThreadsOptions = {},
): ProposalCommentDisplayThread[] => {
  const { includeResolved = true } = options;
  const authorsLookup = createProposalCommentAuthorLookup(options.authors);
  const teamMemberAuthorsLookup = createProposalCommentTeamMemberAuthorLookup(
    options.authorTeamMemberAggregates,
  );

  return threads
    .filter((thread): thread is ProposalCommentThreadEntity & { root: ProposalCommentRootEntity } =>
      Boolean(thread.root),
    )
    .filter(({ root }) => includeResolved || !root.resolvedAt)
    .map(({ root, replies = [] }) => ({
      root: withAuthorDisplayName(root, authorsLookup, teamMemberAuthorsLookup, options),
      replies: replies
        .filter((reply) => reply.threadRootId === undefined || reply.threadRootId === root.id)
        .map((reply) =>
          withAuthorDisplayName(reply, authorsLookup, teamMemberAuthorsLookup, options),
        )
        .sort(compareByCreatedAtAscIdAsc),
      resolved: Boolean(root.resolvedAt),
    }))
    .sort(compareByCreatedAtDescIdDesc);
};

export const buildProposalLastCommentPreviewDisplay = (
  preview: ProposalCommentPreviewOutputView,
  options: ProposalLastCommentPreviewDisplayOptions,
): ProposalLastCommentPreviewDisplay => {
  const authorsLookup = createProposalCommentAuthorLookup(options.authors);
  const teamMemberAuthorsLookup = createProposalCommentTeamMemberAuthorLookup(
    options.authorTeamMemberAggregates,
  );
  const authorDisplay = getProposalCommentAuthorDisplay(
    preview,
    authorsLookup,
    teamMemberAuthorsLookup,
    options,
  );
  const text = normalizeText(preview.text) || options.emptyTextLabel;
  const createdAtDate = toDate(preview.createdAt);
  const timeLabel = formatProposalCommentTimeLabel(preview.createdAt, options);
  const searchText = [authorDisplay.authorDisplayName, text].filter(Boolean).join(" ");

  return {
    id: preview.id,
    ...authorDisplay,
    text,
    timeLabel,
    searchText,
    dateTime: createdAtDate?.toISOString(),
  };
};

const isSameLocalCalendarDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatJustNowLabel = (locale: string) =>
  locale.toLowerCase().startsWith("ru") ? "Только что" : "Just now";

export const formatProposalCommentTimeLabel = (
  createdAt: Date | string | undefined,
  { locale, now }: ProposalCommentTimeLabelOptions,
) => {
  const createdAtDate = toDate(createdAt);

  if (!createdAtDate) return "";

  if (!isSameLocalCalendarDay(createdAtDate, now)) {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(createdAtDate);
  }

  const diffMs = createdAtDate.getTime() - now.getTime();

  if (diffMs >= 0) {
    return formatJustNowLabel(locale);
  }

  const absDiffSeconds = Math.abs(diffMs) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["hour", 60 * 60],
    ["minute", 60],
    ["second", 1],
  ];
  const [unit, secondsPerUnit] =
    units.find(([, seconds]) => absDiffSeconds >= seconds) ?? units[units.length - 1];

  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
    Math.round(diffMs / (secondsPerUnit * 1000)),
    unit,
  );
};
